
/*
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class StateImpl
 * @hideconstructor
 * @classdesc Implementation class for StateApi that is used to control a state
 */
define([
	'underscore',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../lib/@waca/dashboard-common/dist/api/Error',
	'../StateAPI',
	'../PrivateStateAPI',
	'./StateAPISpec'
], (_, APIFactory, APIError, StateAPI, PrivateStateAPI, StateAPISpec) => {
	class StateImpl extends StateAPISpec {

		static ON_CHANGE_ERROR = 'onChangeError';

		constructor(options) {
			super(options);

			this._pendingStatusMap = {};
			this._status = StateAPI.STATUS.CREATED;
			this._error = undefined;

			this._api = APIFactory.createAPI(this, [ StateAPI ]);
			this._privateApi = APIFactory.createAPI(this, [ StateAPI, PrivateStateAPI ]);
			this._statusCountTracker = {};
		}

		getAPI (type) {
			if (type === 'internal'){
				return this._privateApi;
			} else {
				return this._api;
			}
		}

		destroy() {
			this._api = null;
		}

		_isValidStatus(status) {
			return _.values(StateAPI.STATUS).indexOf(status) > -1;
		}

		/**
		 * @implements StateAPI#getStatus
		 */
		getStatus() {
			return this._status;
		}

		/**
		 * @implements StateAPI#setStatus
		 */
		setStatus(status) {
			if (!this._isValidStatus(status)) {
				throw new Error('Invalid status: ' + status);
			}

			this._status = status;

			if(!this._statusCountTracker[status]){
				this._statusCountTracker[status] = 1;
			}else {
				this._statusCountTracker[status]++;
			}

			// resolve all promises waiting for the status change
			if (_.isArray(this._pendingStatusMap[status])) {
				while (this._pendingStatusMap[status].length > 0) {
					this._pendingStatusMap[status][0].resolve(this._getResolveState(status));
					this._pendingStatusMap[status].splice(0, 1);
				}
			}
		}

		_getResolveState(status){
			return {
				status: status,
				count: this._statusCountTracker[status]
			};
		}

		/**
		 * @implements StateAPI#whenStatusChanges
		 */
		whenStatusChanges(status) {
			if (!this._isValidStatus(status)) {
				throw new Error('Invalid status: ' + status);
			}

			// simply resolve if current status is the expected status
			if (status === this._status) {
				return Promise.resolve(this._getResolveState(status));
			}

			// keep track of all promises per each status
			if (!this._pendingStatusMap[status]) {
				this._pendingStatusMap[status] = [];
			}
			return new Promise((resolve, reject) => {
				this._pendingStatusMap[status].push({
					'resolve': resolve,
					'reject': reject
				});
			});
		}

		/**
		 * @implements StateAPI#getError
		 */
		getError() {
			return this._error;
		}

		/**
		 * @implements StateAPI#setError
		 */
		setError(error) {
			// use != to check for both null and undefined
			if (error != null && !(error instanceof APIError)){
				error = new APIError(error);
			}
			this._error = error;
			this.invokeCallbacks(StateImpl.ON_CHANGE_ERROR, error);
		}

		/**
		 * @implements StateAPI#clearError
		 */
		clearError() {
			this.setError();
		}

		/**
		 * @implements StateAPI#onChangeError
		 */
		onChangeError(callback) {
			this.registerCallback(StateImpl.ON_CHANGE_ERROR, callback);
		}
	}

	return StateImpl;
});